import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Mail, Phone, Building2, User, MessageSquare, 
  X, Send, Loader2, CheckCircle, MapPin, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactModal = ({ isOpen, onClose, onSubmitSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organisation: '',
    website: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  // Validate Australian phone number
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    const mobileRegex = /^(04\d{8}|(\+61|0061)4\d{8})$/;
    const landlineRegex = /^(0[2-9]\d{8}|(\+61|0061)[2-9]\d{8})$/;
    return mobileRegex.test(cleaned) || landlineRegex.test(cleaned);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        organisation: '',
        website: '',
        subject: '',
        message: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid Australian phone number';
    }
    if (!formData.organisation.trim()) newErrors.organisation = 'Organisation is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // Real-time phone validation
    if (field === 'phone' && value && !validatePhone(value)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid Australian phone number' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.organisation,
          website: formData.website,
          subject: formData.subject,
          message: formData.message,
          created_at: new Date().toISOString()
        })
      });
      
      if (!res.ok) throw new Error('Failed to submit');
      
      setSubmitted(true);
      // Store contact submission in localStorage to unlock gated content
      localStorage.setItem('contact_submitted', 'true');
      localStorage.setItem('contact_email', formData.email);
      
      // Also create AI lead entry for AI demo access
      try {
        const aiLeadRes = await fetch(`${API_URL}/api/ai/capture-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.fullName,
            email: formData.email,
            company: formData.organisation || 'Not specified',
            phone: formData.phone,
            feature_interest: 'general'
          })
        });
        if (aiLeadRes.ok) {
          const aiLeadData = await aiLeadRes.json();
          localStorage.setItem('ai_lead_id', aiLeadData.lead_id);
        }
      } catch (aiErr) {
        console.log('AI lead creation optional:', aiErr);
      }
      
      // Notify parent that form was submitted successfully
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      toast.success('Message sent successfully!');
    } catch (e) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-navy to-tech-blue p-6 text-white relative">
          <button 
            onClick={onClose}
            className="absolute right-4 top-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold">Contact Us</h2>
          <p className="text-white/80 mt-1">We'd love to hear from you</p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for reaching out. We'll get back to you within 24 hours.
              </p>
              <Button onClick={onClose} className="bg-tech-blue hover:bg-tech-blue/90">
                Done
              </Button>
            </div>
          ) : (
            <>
              {/* Contact Info Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <Mail className="mx-auto text-tech-blue mb-2" size={20} />
                  <p className="text-xs text-gray-600">info@getcare360.com.au</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <Phone className="mx-auto text-tech-blue mb-2" size={20} />
                  <p className="text-xs text-gray-600">1800 951 GET</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <MapPin className="mx-auto text-tech-blue mb-2" size={20} />
                  <p className="text-xs text-gray-600">Australia Wide</p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div className={`relative transition-all duration-300 ${focusedField === 'fullName' ? 'scale-[1.02]' : ''}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        data-testid="contact-fullname-input"
                        placeholder="Your full name"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        onFocus={() => setFocusedField('fullName')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 ${errors.fullName ? 'border-red-400 bg-red-50' : focusedField === 'fullName' ? 'border-tech-blue bg-tech-blue/5' : ''}`}
                      />
                    </div>
                    {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                  </div>

                  {/* Email */}
                  <div className={`relative transition-all duration-300 ${focusedField === 'email' ? 'scale-[1.02]' : ''}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        data-testid="contact-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 ${errors.email ? 'border-red-400 bg-red-50' : focusedField === 'email' ? 'border-tech-blue bg-tech-blue/5' : ''}`}
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Phone */}
                  <div className={`relative transition-all duration-300 ${focusedField === 'phone' ? 'scale-[1.02]' : ''}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        data-testid="contact-phone-input"
                        type="tel"
                        placeholder="04XX XXX XXX"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 ${errors.phone ? 'border-red-400 bg-red-50' : focusedField === 'phone' ? 'border-care-teal bg-care-teal/5' : ''}`}
                      />
                    </div>
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  {/* Organisation */}
                  <div className={`relative transition-all duration-300 ${focusedField === 'organisation' ? 'scale-[1.02]' : ''}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organisation <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        data-testid="contact-organisation-input"
                        placeholder="Your organisation"
                        value={formData.organisation}
                        onChange={(e) => handleInputChange('organisation', e.target.value)}
                        onFocus={() => setFocusedField('organisation')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 ${errors.organisation ? 'border-red-400 bg-red-50' : focusedField === 'organisation' ? 'border-tech-blue bg-tech-blue/5' : ''}`}
                      />
                    </div>
                    {errors.organisation && <p className="text-red-500 text-xs mt-1">{errors.organisation}</p>}
                  </div>
                </div>

                {/* Organisation Website */}
                <div className={`relative transition-all duration-300 ${focusedField === 'website' ? 'scale-[1.02]' : ''}`}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organisation Website
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      data-testid="contact-website-input"
                      type="url"
                      placeholder="https://www.yourcompany.com.au"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      onFocus={() => setFocusedField('website')}
                      onBlur={() => setFocusedField(null)}
                      className={`pl-10 ${focusedField === 'website' ? 'border-tech-blue bg-tech-blue/5' : ''}`}
                    />
                  </div>
                </div>

                {/* Subject */}
                <div className={`relative transition-all duration-300 ${focusedField === 'subject' ? 'scale-[1.02]' : ''}`}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <Input
                    data-testid="contact-subject-input"
                    placeholder="How can we help?"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    onFocus={() => setFocusedField('subject')}
                    onBlur={() => setFocusedField(null)}
                    className={focusedField === 'subject' ? 'border-tech-blue bg-tech-blue/5' : ''}
                  />
                </div>

                {/* Message */}
                <div className={`relative transition-all duration-300 ${focusedField === 'message' ? 'scale-[1.02]' : ''}`}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 text-gray-400" size={18} />
                    <textarea
                      data-testid="contact-message-input"
                      placeholder="Tell us more about your needs..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg resize-none h-32 transition-all ${
                        errors.message ? 'border-red-400 bg-red-50' : 
                        focusedField === 'message' ? 'border-tech-blue bg-tech-blue/5 ring-2 ring-tech-blue/20' : 
                        'border-gray-200 focus:ring-2 focus:ring-tech-blue/20 focus:border-tech-blue'
                      }`}
                    />
                  </div>
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <Button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={submitting || errors.phone}
                  className="w-full bg-tech-blue hover:bg-tech-blue/90 py-6 text-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
